/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy at http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND.
 */

export const DAY_MS = 24 * 60 * 60 * 1000;
export const MAX_STAY_NIGHTS = 30;
export const SYNTHETIC_TAX_RATE = 0.146;
export const STEP_UP_THRESHOLD_CENTS = 50_000;

export const hotels = Object.freeze([
  {
    id: 'chi-river-north',
    name: 'AnyHotel River North',
    city: 'Chicago',
    country: 'US',
    eyebrow: 'City stay',
    description: 'Warm modern rooms a short walk from the river and Michigan Avenue.',
    accent: 'navy',
    roomTypes: [
      {
        id: 'city-king',
        name: 'City King',
        nightlyRate: { currency: 'USD', value: 18900 },
        maxGuests: 2,
      },
      {
        id: 'corner-suite',
        name: 'Corner Suite',
        nightlyRate: { currency: 'USD', value: 32900 },
        maxGuests: 3,
      },
    ],
  },
  {
    id: 'sea-waterfront',
    name: 'AnyHotel Waterfront',
    city: 'Seattle',
    country: 'US',
    eyebrow: 'Harbor views',
    description: 'A calm base near Pike Place with wide views across Elliott Bay.',
    accent: 'teal',
    roomTypes: [
      {
        id: 'harbor-queen',
        name: 'Harbor Queen',
        nightlyRate: { currency: 'USD', value: 21900 },
        maxGuests: 2,
      },
      {
        id: 'sound-suite',
        name: 'Sound Suite',
        nightlyRate: { currency: 'USD', value: 38900 },
        maxGuests: 4,
      },
    ],
  },
  {
    id: 'mia-south-beach',
    name: 'AnyHotel South Beach',
    city: 'Miami',
    country: 'US',
    eyebrow: 'Ocean escape',
    description: 'Sunlit rooms, a shaded courtyard, and the ocean one block away.',
    accent: 'coral',
    roomTypes: [
      {
        id: 'courtyard-king',
        name: 'Courtyard King',
        nightlyRate: { currency: 'USD', value: 24900 },
        maxGuests: 2,
      },
      {
        id: 'ocean-studio',
        name: 'Ocean Studio',
        nightlyRate: { currency: 'USD', value: 44900 },
        maxGuests: 3,
      },
    ],
  },
]);

export class BookingValidationError extends Error {
  constructor(message) {
    super(message);
    this.name = 'ValidationError';
  }
}

export function getCatalog() {
  return hotels;
}

export function findRoom(hotelId, roomTypeId) {
  const hotel = hotels.find((candidate) => candidate.id === hotelId);
  const roomType = hotel?.roomTypes.find((candidate) => candidate.id === roomTypeId);
  return hotel && roomType ? { hotel, roomType } : undefined;
}

function parseDate(value, fieldName) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    throw new BookingValidationError(`${fieldName} must use YYYY-MM-DD`);
  }

  const timestamp = Date.parse(`${value}T00:00:00.000Z`);
  if (!Number.isFinite(timestamp) || new Date(timestamp).toISOString().slice(0, 10) !== value) {
    throw new BookingValidationError(`${fieldName} is not a valid date`);
  }
  return timestamp;
}

export function nightsBetween(checkIn, checkOut) {
  const start = parseDate(checkIn, 'checkIn');
  const end = parseDate(checkOut, 'checkOut');
  const nights = (end - start) / DAY_MS;
  if (!Number.isInteger(nights) || nights < 1 || nights > MAX_STAY_NIGHTS) {
    throw new BookingValidationError(`stay must be between 1 and ${MAX_STAY_NIGHTS} nights`);
  }
  return nights;
}

export function calculateQuote(input, thresholdCents = STEP_UP_THRESHOLD_CENTS) {
  const room = findRoom(input?.hotelId, input?.roomTypeId);
  if (!room) {
    throw new BookingValidationError('Select an available hotel and room.');
  }

  const guests = Number(input.guests);
  if (!Number.isInteger(guests) || guests < 1 || guests > room.roomType.maxGuests) {
    throw new BookingValidationError(
      `This room accommodates up to ${room.roomType.maxGuests} guests.`,
    );
  }

  const nights = nightsBetween(input.checkIn, input.checkOut);
  if (!Number.isSafeInteger(thresholdCents) || thresholdCents < 0) {
    throw new Error('step-up threshold must be a non-negative integer amount');
  }

  const roomSubtotal = room.roomType.nightlyRate.value * nights;
  const taxes = Math.round(roomSubtotal * SYNTHETIC_TAX_RATE);
  const total = {
    currency: room.roomType.nightlyRate.currency,
    value: roomSubtotal + taxes,
  };

  return {
    hotel: {
      id: room.hotel.id,
      name: room.hotel.name,
      city: room.hotel.city,
      country: room.hotel.country,
      eyebrow: room.hotel.eyebrow,
      description: room.hotel.description,
      accent: room.hotel.accent,
    },
    roomType: {
      id: room.roomType.id,
      name: room.roomType.name,
      nightlyRate: room.roomType.nightlyRate,
    },
    checkIn: input.checkIn,
    checkOut: input.checkOut,
    nights,
    guests,
    roomSubtotal,
    taxes,
    total,
    stepUpRequired: total.value > thresholdCents,
  };
}
